import React, { useCallback, useEffect, useMemo, useState } from "react";
import { eventService } from "../lib/api/event";
import { serverImgUrl } from '../config/constants';
import Swal from "sweetalert2";
import { Table, Button, Spinner, Form } from "react-bootstrap";
import { routes } from "../routes/routes-names";
import { useNavigate } from "react-router-dom";
import { organizationService } from "../lib/api/organization";
import PdfViewer from "./PdfViewer";
import moment from "moment";


 interface AttendanceRespPoint {
  eventDate: string;
  weightage: number;
}
interface eventType {
  id: number;
  nameOfEvent: string;
  organizationName: string;
  startDate?: string;
  endDate?: string;
  createdon?: string;
  attendanceRespPoints:AttendanceRespPoint[];
  eventCategory: string;
  eventType: string;
  department: string;
  speciality: string;
  paymentStatus: string;
  organizationType: string;
  programSheet1: string;
  programSheet2: string;
  programSheet3: string;
  programSheet4: string;
  programSheet5: string;
  letterHost: string;
  remarks?: string;
  verifiedDate?: string;
  verificationDate?: string;
  approvedDate?: string;
  approvalDate?: string;
  orderAmount?: string;
  paymentDateTime?: string;
}

type StatusCode = 'pen' | 'ver' | 'com' | 'apr' | 'rej';

const STATUS_OPTIONS: { value: StatusCode; label: string }[] = [
  { value: "pen", label: "Event Approval Pending" },
  { value: "ver", label: "Event Verified" },
  { value: "com", label: "Event Completed" },
  { value: "apr", label: "Payment Completed" },
  { value: "rej", label: "Event Rejected" },
];

const EventApprovalPage: React.FC = () => {

  const navigate = useNavigate();
 
  // Store edits per event id so switching rows doesn't leak values.
  const [attendancePointsByEvent, setAttendancePointsByEvent] = useState<Record<number, AttendanceRespPoint[]>>({});

  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [eventData, setEventData] = useState<eventType[]>([]);
  const [disablebtn, setDisablebtn] = useState(false);
  const [remarks, setRemarks] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeStatus, setActiveStatus] = useState<StatusCode>('pen');
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(15);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [orgData, setOrgData] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const selectedOrgId = useMemo(() => {
    if (!selectedOrg) return undefined;
    const parsed = Number(selectedOrg);
    return Number.isNaN(parsed) ? undefined : parsed;
  }, [selectedOrg]);
  const toggleRow = (id: number) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  const parseDateValue = (value?: string) => {
    if (!value) return null;
    const parsed = moment(value, ["DD/MM/YYYY", "DD-MM-YYYY", "YYYY-MM-DD", moment.ISO_8601], true);
    return parsed.isValid() ? parsed : null;
  };

  const getStatusMoment = (event: eventType, status: StatusCode) => {
    if (status === "apr") return parseDateValue(event.paymentDateTime);
    if (status === "ver") return parseDateValue(event.verifiedDate || event.verificationDate);
    if (status === "rej") return parseDateValue(event.verifiedDate || event.verificationDate);
    if (status === "pen") return parseDateValue(event.createdon);
    return parseDateValue(event.startDate);
  };

  const sortEventsByStatus = useCallback((list: eventType[], status: string) => {
    const getSortTimestamp = (event: eventType) => {
      const parsed = getStatusMoment(event, status as StatusCode);
      return parsed ? parsed.valueOf() : null;
    };
    return [...list].sort((a, b) => {
      const aTs = getSortTimestamp(a);
      const bTs = getSortTimestamp(b);
      if (aTs === null && bTs === null) return 0;
      if (aTs === null) return 1;
      if (bTs === null) return -1;
      return bTs - aTs;
    });
  }, []);

  const getEventDetailsByOrgId = useCallback(async (status: any) => {
    try {
      setEventData([]);
      setLoading(true);

      // If an organization is chosen, keep the existing org-scoped flow.
      if (selectedOrgId !== undefined && selectedOrgId !== null) {
        const { data } = await eventService.getEventsbyOrgidAndStatus(selectedOrgId, status);
        if (data.length > 0) {
          setEventData(sortEventsByStatus(data, status));
        }
        return;
      }

      // No organization selected: fall back to global verification list.
      const { data } = await eventService.getEventDetailsforVerification(status);
      if (data?.length > 0) {
        setEventData(sortEventsByStatus(data, status));
      }
    } catch (err) {
      console.log('error getOrganizationDetails', err);
    } finally {
      setLoading(false);
    }
  }, [selectedOrgId, sortEventsByStatus]);

  const getAllApprovedOrgs = async () => {
    try {
      const { data } = await organizationService.getOrganizationDetailsforVerification('apr');
      if (data.length > 0) {
        setOrgData(data);
      }

    } catch {
    }
  };
  useEffect(() => {
    //getEventDetails('pen');
    getAllApprovedOrgs();
  }, []);

  const submit = useCallback(async (status: any, id: any, fallbackPoints: AttendanceRespPoint[]) => {
    if (status) {
      setDisablebtn(true);
      const eventApprovalInfo = {
        // If the reviewer hasn't changed anything for this event, fall back to its original points.
        attendancePoints: attendancePointsByEvent[id]?.length ? attendancePointsByEvent[id] : fallbackPoints,
        approval_status: status,
        remarks: remarks
      }
      const { success } = await eventService.updateEventApprovals(id, eventApprovalInfo);
      if (success) {
        let msg = "Event Details Updated";
        //getEventDetails('pen');

        Swal.fire({
          title: "",
          text: msg,
          icon: status === 'rej' ? "error" : "success",
          confirmButtonText: "OK",
        }).then(async (result) => {
          if (result.isConfirmed) {
            setDisablebtn(false);
          }
        });
        navigate(routes.adminLayout);
      }
      else {
        Swal.fire({
          title: "",
          text: "org registration rejected",
          icon: "error",
          confirmButtonText: "OK",
        }).then(async (result) => {
        });
      }
    }
    else {
      Swal.fire({
        //title: "Error",
        text: "something went wrong",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  }, [attendancePointsByEvent, remarks]);

  const handleOrgChange = async (event: any) => {
    const raw = event.target.value;
    setSelectedOrg(raw);
    setCurrentPage(1);
  };

  const clearOrganization = () => {
    setSelectedOrg('');
    setCurrentPage(1);
  };

  const clearAdvancedFilters = () => {
    setSearchQuery('');
    setActiveStatus('pen');
    setCurrentPage(1);
  };

  const handleStatusChange = (status: StatusCode) => {
    setActiveStatus(status);
    setCurrentPage(1);
    setExpandedRow(null);
    setAttendancePointsByEvent({});
    setRemarks('');
  };

  useEffect(() => {
    // Immediately load pending events for the selected organization (or global) so the table is populated.
    getEventDetailsByOrgId(activeStatus);
  }, [selectedOrgId, getEventDetailsByOrgId, activeStatus]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const filteredEventData = useMemo(() => {
    const normalizedSearch = searchQuery.trim().toLowerCase();
    return eventData.filter((event) => {
      if (normalizedSearch) {
        const haystack = `${event.organizationName || ""} ${event.nameOfEvent || ""}`.toLowerCase();
        if (!haystack.includes(normalizedSearch)) return false;
      }
      return true;
    });
  }, [eventData, searchQuery]);

  const totalPages = Math.ceil(filteredEventData.length / recordsPerPage);

  const paginatedData = () => {
    const start = (currentPage - 1) * recordsPerPage;
    return filteredEventData.slice(start, start + recordsPerPage);
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handlePointsChange = (eventId: number, sourcePoints: AttendanceRespPoint[], index: number, value: number) => {
    // Use existing edits for this event if present; otherwise start from its source list.
    const base = attendancePointsByEvent[eventId]?.length ? attendancePointsByEvent[eventId] : sourcePoints;
    const updated = base.map((p, i) => i === index ? { ...p, weightage: value } : p);
    setAttendancePointsByEvent((prev) => ({ ...prev, [eventId]: updated }));
  };
  const showVerifiedDateColumn = activeStatus === "ver";
  const showApprovedDateColumn = false;
  const showApprovedPaymentColumns = activeStatus === "apr";
  const showCompletedOnColumn = activeStatus === "com";
  const showRejectedDateColumn = activeStatus === "rej";
  const showRejectedRemarksColumn = activeStatus === "rej";
  const showEventCreatedColumn = activeStatus === "pen";
  const optionalColumnCount =
    (showEventCreatedColumn ? 1 : 0) +
    (showVerifiedDateColumn ? 1 : 0) +
    (showApprovedDateColumn ? 1 : 0) +
    (showApprovedPaymentColumns ? 2 : 0) +
    (showCompletedOnColumn ? 1 : 0) +
    (showRejectedDateColumn ? 1 : 0) +
    (showRejectedRemarksColumn ? 1 : 0);
  const formatDateOnly = (value?: string) => {
    if (!value) return "--";
    const m = moment(value, [moment.ISO_8601, "YYYY-MM-DD", "DD-MM-YYYY", "DD/MM/YYYY"], true);
    return m.isValid() ? m.format("DD-MM-YYYY") : "--";
  };
  const formatPaymentDateTime = (value?: string) => {
    if (!value) return "--";
    const m = moment(value, [moment.ISO_8601, "YYYY-MM-DD", "DD-MM-YYYY", "DD/MM/YYYY"], true);
    return m.isValid() ? m.format("DD-MM-YYYY") : value;
  };
  const getStatusDateValue = (event: eventType) => {
    if (showVerifiedDateColumn) {
      return formatDateOnly(event.verifiedDate || event.verificationDate);
    }
    if (showApprovedDateColumn) {
      return formatDateOnly(event.approvedDate || event.approvalDate);
    }
    if (showCompletedOnColumn) {
      return formatDateOnly(event.verifiedDate || event.verificationDate);
    }
    if (showRejectedDateColumn) {
      return formatDateOnly(event.verifiedDate || event.verificationDate);
    }
    return "--";
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center mt-5">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <h3 className="mb-3 text-center cme-header">Event Approvals </h3>

      <div className="card mb-3 p-3 event-approval-filters">
        <div className="row g-3 align-items-end">
          <div className="col-md-5">
            <Form.Label className="fw-semibold">Organization</Form.Label>
            <div className="d-flex gap-2">
              <Form.Select
                name="organizationType"
                value={selectedOrg}
                onChange={handleOrgChange}
                className="org-name-wrap"
              >
                <option value="">--All organizations--</option>
                {orgData.map((orgtype) => (
                  <option key={orgtype.id} value={orgtype.id}>
                    {orgtype.organizationName}
                  </option>
                ))}
              </Form.Select>
              <Button variant="outline-secondary" size="sm" onClick={clearOrganization}>
                Reset
              </Button>
            </div>
          </div>
        </div>

        <div className="row g-3 pt-3">
          <div className="col-md-4">
            <Form.Label className="fw-semibold">Search</Form.Label>
            <Form.Control
              type="text"
              className="event-filter-search-input"
              placeholder="Organization or Event name"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="col-md-4">
            <Form.Label className="fw-semibold">Status</Form.Label>
            <Form.Select
              value={activeStatus}
              onChange={(e) => handleStatusChange(e.target.value as StatusCode)}
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </div>
          <div className="col-md-2 d-flex align-items-end">
            <Button variant="outline-secondary" className="w-100 event-filter-clear-btn" onClick={clearAdvancedFilters}>
              Clear Filters
            </Button>
          </div>
        </div>
      </div>
      <Table bordered hover responsive>
        <thead className="table-primary">
          <tr>
            <th style={{ width: "50px" }}></th>
            <th>Organization Name</th>
            <th>Event Name</th>
            <th>Event Type</th>
            <th>Event speciality</th>
            <th>startDate</th>
            <th>endDate</th>
            {showEventCreatedColumn && <th>Created On</th>}
            {showVerifiedDateColumn && <th>Verified On</th>}
            {showApprovedDateColumn && <th>Approved On</th>}
            {showApprovedPaymentColumns && <th>Amount Paid</th>}
            {showApprovedPaymentColumns && <th>Payment Date</th>}
            {showCompletedOnColumn && <th>Completed On</th>}
            {showRejectedDateColumn && <th>Rejected On</th>}
            {showRejectedRemarksColumn && <th>Remarks</th>}
          </tr>
        </thead>
        <tbody>
          {paginatedData().length === 0 && (
            <tr>
              <td colSpan={8 + optionalColumnCount} className="text-center text-muted">No events found for this filter</td>
            </tr>
          )}
          {paginatedData().map((event) => (
            <React.Fragment key={event.id}>
              <tr>
                <td
                  onClick={() => toggleRow(event.id)}
                  style={{ cursor: "pointer", textAlign: "center" }}
                >
                  {expandedRow === event.id ? (
                    <i className="bi bi-chevron-up"></i>
                  ) : (
                    <i className="bi bi-chevron-down"></i>
                  )}
                </td>
                <td>{event.organizationName}</td>
                <td>{event.nameOfEvent}</td>
                <td>{event.eventType}</td>
                <td>{event.speciality}</td>
                <td>{formatDateOnly(event.startDate)}</td>
                <td>{formatDateOnly(event.endDate)}</td>
                {showEventCreatedColumn && (
                  <td>{formatDateOnly(event.createdon)}</td>
                )}
                {(showVerifiedDateColumn || showApprovedDateColumn || showCompletedOnColumn || showRejectedDateColumn) && (
                  <td>{getStatusDateValue(event)}</td>
                )}
                {showApprovedPaymentColumns && (
                  <td>{event.orderAmount || "--"}</td>
                )}
                {showApprovedPaymentColumns && (
                  <td>{formatPaymentDateTime(event.paymentDateTime)}</td>
                )}
                {showRejectedRemarksColumn && (
                  <td>{event.remarks || "--"}</td>
                )}
              </tr>
              {expandedRow === event.id && (
                <>
                  <tr>
                    <td colSpan={2}>
                      <strong>Program Sheet1:</strong>
                      <div className="d-flex flex-wrap gap-2 mt-2">
                        {event.programSheet1 &&
                          <div className="col">
                            <div className="drag-img-box d-flex align-items-center justify-content-center">
                              <p className="d-flex align-items-center">{<PdfViewer fileUrl={serverImgUrl + '/event/' + event.programSheet1} />}
                              </p>
                            </div>
                          </div>}
                      </div>

                    </td>
                    <td colSpan={2}>
                      <strong>Program Sheet2:</strong>
                      <div className="d-flex flex-wrap gap-2 mt-2">
                        {event.programSheet2 &&
                          <div className="col">
                            <div className="drag-img-box d-flex align-items-center justify-content-center">
                              <p className="d-flex align-items-center">{<PdfViewer fileUrl={serverImgUrl + '/event/' + event.programSheet2} />}

                              </p>
                            </div>
                          </div>}
                      </div>
                    </td>
                    <td colSpan={2}>
                      <strong>Program Sheet3:</strong>
                      <div className="d-flex flex-wrap gap-2 mt-2">
                        {event.programSheet3 &&
                          <div className="col">
                            <div className="drag-img-box d-flex align-items-center justify-content-center">
                              <p className="d-flex align-items-center">{<PdfViewer fileUrl={serverImgUrl + '/event/' + event.programSheet3} />}
                              </p>
                            </div>
                          </div>}
                      </div>

                    </td>
                  </tr>
                  <tr>
                    <td colSpan={2}>
                      <strong>Program Sheet4:</strong>
                      <div className="d-flex flex-wrap gap-2 mt-2">
                        {event.programSheet4 &&
                          <div className="col">
                            <div className="drag-img-box d-flex align-items-center justify-content-center">
                              <p className="d-flex align-items-center">{<PdfViewer fileUrl={serverImgUrl + '/event/' + event.programSheet4} />}

                              </p>
                            </div>
                          </div>}
                      </div>

                    </td>
                    <td colSpan={2}>
                      <strong>Program Sheet5:</strong>
                      <div className="d-flex flex-wrap gap-2 mt-2">
                        {event.programSheet5 &&
                          <div className="col">
                            <div className="drag-img-box d-flex align-items-center justify-content-center">
                              <p className="d-flex align-items-center">{<PdfViewer fileUrl={serverImgUrl + '/event/' + event.programSheet5} />}

                              </p>
                            </div>
                          </div>}
                      </div>

                    </td>
                    <td colSpan={2}>
                      <strong>letter Host:</strong>
                      <div className="d-flex flex-wrap gap-2 mt-2">
                        {event.letterHost &&
                          <div className="col">
                            <div className="drag-img-box d-flex align-items-center justify-content-center">
                              <p className="d-flex align-items-center">{<PdfViewer fileUrl={serverImgUrl + '/event/' + event.letterHost} />}

                              </p>
                            </div>
                          </div>}
                      </div>

                    </td>
                  </tr>
               
                  <tr>
                        {event.paymentStatus !== 'pen' &&<td colSpan={6}>
                         <table> <tr>
                            <th style={{ width: "50%" }}>Date</th>
                            <th style={{ width: "50%" }}>Points</th>
                          </tr>
                           {event.attendanceRespPoints && event.attendanceRespPoints.length>0 && ((attendancePointsByEvent[event.id]?.length ? attendancePointsByEvent[event.id] : event.attendanceRespPoints)).map((item, index) => (
                            <tr key={item.eventDate}>
                              <td className="fw-semibold">
                                {item.eventDate}
                              </td>

                              <td>
                                <Form.Control
                                  type="number"
                                  disabled={true}
                                  min={0}
                                  max={10}
                                  value={item.weightage}
                                  
                                />
                              </td>
                            </tr>
                            
                          ))}
                          </table>
                          </td>
                        }
                    {event.paymentStatus === 'pen' &&
                      <td colSpan={6}>
                         <table> <tr>
                            <th style={{ width: "50%" }}>Date</th>
                            <th style={{ width: "50%" }}>Points</th>
                          </tr>
                           {event.attendanceRespPoints&& event.attendanceRespPoints.length>0&&((attendancePointsByEvent[event.id]?.length ? attendancePointsByEvent[event.id] : event.attendanceRespPoints)).map((item, index) => (
                            <tr key={item.eventDate}>
                              <td className="fw-semibold">
                                {item.eventDate}
                              </td>

                              <td>
                                  <Form.Control
                                    type="number"
                                    min={0}
                                    max={10}
                                    value={item.weightage}
                                    onFocus={(e) => e.target.select()}
                                    onChange={(e) =>
                                      handlePointsChange(event.id, event.attendanceRespPoints, index, Number(e.target.value))
                                    }
                                  />
                              </td>
                            </tr>
                          ))}
                          </table>
                        <div className="card-footer">
                          <div className="mb-3">
                            <label htmlFor="" className='mb-2'>Reason <span className='fs-12'>{'(Enter reason if you are rejecting application)'}</span></label>
                            <textarea className='form-control fs-14' onChange={(e) => setRemarks(e.target.value)} name="" id="" placeholder='Enter Reason'></textarea>
                          </div>
                          <div className='d-flex'>
                            <div className="col">
                              <button type="submit"
                                disabled={disablebtn}
                                onClick={() => {
                                  submit('rej', event.id, event.attendanceRespPoints || []);
                                }} className='btn btn-danger'><i className="bi-x-circle"></i> Not Accepted</button>
                            </div>
                            <div className="col text-end">
                              <button type="submit"
                                disabled={disablebtn}
                                onClick={() => {
                                  submit('ver', event.id, event.attendanceRespPoints || []);
                                }} className='btn btn-success'><i className="bi-check-circle"></i> Verified</button>
                            </div>
                          </div>
                        </div>

                      </td>
                    }
                  </tr>
                </>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </Table>
      <div className="d-flex align-items-center flex-wrap gap-3 mb-2 mt-2 w-100">
        <div className="d-flex align-items-center gap-2">
          <Form.Label className="mb-0 fw-semibold">Rows per page</Form.Label>
          <Form.Select
            size="sm"
            value={recordsPerPage}
            onChange={(e) => {
              setRecordsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            style={{ width: "auto" }}
          >
            {[10, 15, 25, 50, 100].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </Form.Select>
        </div>

        {totalPages > 1 && (
          <div className="d-flex justify-content-center align-items-center gap-2 ms-auto">
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
            >
              « First
            </Button>
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              ‹ Prev
            </Button>
            <Form.Select
              size="sm"
              value={currentPage}
              onChange={(e) => handlePageChange(Number(e.target.value))}
              style={{ width: "auto" }}
            >
              {[...Array(totalPages)].map((_, i) => (
                <option key={i} value={i + 1}>
                  Page {i + 1} of {totalPages}
                </option>
              ))}
            </Form.Select>
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next ›
            </Button>
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
            >
              Last »
            </Button>
          </div>
        )}
      </div>

    </div>
  );
};

export default EventApprovalPage;
